/**
 * The details view of the calendar event sidebar: the former EventDetailsDialog ported onto
 * the drawer surface (EventSidebar hosts it) - full time/location/room facts, the room link
 * when the event carries a roomSlug, the scheduling-intent badges, Edit (local events only,
 * switches the same drawer to the form view) and deletion behind the confirm gate. Imported
 * rows show their source badge and a read-only note instead - the service answers 409
 * read_only_source to any mutation, the UI mirrors that rule.
 */

import { useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MapPin, Pencil, Trash2, Users, Video } from 'lucide-react';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { CalendarApiError, deleteEvent, type CalendarEvent } from './api';
import { eventEnd, eventStart, formatTime } from './month';
import { showToast } from './Toast';

/** Localized label of an event's source ('local' maps to the "created by you" badge). */
export function sourceLabel(source: string, t: (key: string) => string): string {
  if (source === 'local') {
    return t('calendar.list.local_badge');
  }
  if (source === 'caldav') {
    return t('calendar.list.source_caldav');
  }
  if (source === 'ews') {
    return t('calendar.list.source_ews');
  }
  return t('calendar.list.source_ad');
}

function formatEventTime(event: CalendarEvent, locale: string): string {
  const start = eventStart(event);
  const end = eventEnd(event);
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  }).format(start);
  if (event.allDay) {
    return day;
  }
  return `${day} · ${formatTime(start)}–${formatTime(end)}`;
}

export interface EventSidebarDetailsProps {
  readonly event: CalendarEvent;
  readonly token: string;
  /** Closes the drawer (close button and the success paths of delete). */
  readonly onClose: () => void;
  /** Switches the same drawer to the edit form (local events only). */
  readonly onEdit: () => void;
  /** Called after a successful delete so the page can refresh. */
  readonly onDeleted: (id: number) => void;
  /** Navigates the SPA to /r/<slug>; required for the room link. */
  readonly onOpenRoom?: ((slug: string) => void) | undefined;
}

export function EventSidebarDetails({
  event,
  token,
  onClose,
  onEdit,
  onDeleted,
  onOpenRoom,
}: EventSidebarDetailsProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete(): Promise<void> {
    if (deleting) {
      return;
    }
    setDeleting(true);
    try {
      await deleteEvent(token, event.id);
      showToast('success', t('calendar.details.deleted_toast'), event.title);
      onDeleted(event.id);
      onClose();
    } catch (error: unknown) {
      // Already gone (another tab deleted it, 404 not_found): the goal is reached, close clean.
      if (error instanceof CalendarApiError && error.status === 404) {
        showToast('success', t('calendar.details.deleted_toast'), event.title);
        onDeleted(event.id);
        onClose();
        return;
      }
      showToast('error', t('calendar.details.delete_failed'), event.title);
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="event-sidebar-details">
      <SheetHeader className="border-b border-border px-5 pb-4 pr-12 pt-5">
        <SheetTitle className="text-[16px] font-semibold leading-snug tracking-tight text-foreground">
          {event.title}
        </SheetTitle>
        <SheetDescription className="flex flex-wrap items-center gap-2 pt-1 text-[12.5px] text-muted-foreground">
          <Badge
            variant={event.source === 'local' ? 'default' : 'outline'}
            className="px-1.5 py-0 text-[10.5px]"
            data-testid="event-source-badge"
          >
            {sourceLabel(event.source, t)}
          </Badge>
          <span className="tabular-nums">{formatEventTime(event, locale)}</span>
        </SheetDescription>
      </SheetHeader>

      <div className="scroll-thin min-h-0 flex-1 space-y-3.5 overflow-y-auto px-5 py-5 text-[13px]">
        {event.location !== null ? (
          <div className="flex items-start gap-2.5">
            <MapPin aria-hidden className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                {t('calendar.details.location')}
              </p>
              <p className="mt-0.5 break-words text-foreground">{event.location}</p>
            </div>
          </div>
        ) : null}

        {event.roomSlug !== null ? (
          <div className="flex items-start gap-2.5">
            <Video aria-hidden className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                {t('calendar.details.room')}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-1 h-8 gap-1.5 border-border text-[12.5px]"
                onClick={() => onOpenRoom?.(event.roomSlug ?? '')}
              >
                <Video aria-hidden className="size-3.5" />
                {t('calendar.details.open_room')} · /r/{event.roomSlug}
              </Button>
            </div>
          </div>
        ) : null}

        {/* Scheduling intents (CR-21): read-only summary badges of what the linked room
            inherits while this event is current. */}
        {event.recordingIntent || event.lobbyIntent ? (
          <div className="flex flex-wrap gap-1.5" data-testid="event-intents">
            {event.recordingIntent ? (
              <Badge
                variant="outline"
                className="px-1.5 py-0 text-[10.5px]"
                data-testid="event-intent-recording"
              >
                {t('calendar.event.details.intent_recording')}
              </Badge>
            ) : null}
            {event.lobbyIntent ? (
              <Badge
                variant="outline"
                className="px-1.5 py-0 text-[10.5px]"
                data-testid="event-intent-lobby"
              >
                {t('calendar.event.details.intent_lobby')}
              </Badge>
            ) : null}
          </div>
        ) : null}

        {event.participants.length > 0 ? (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {t('calendar.details.participants')}
            </p>
            <ul
              className="mt-1 flex flex-wrap gap-1.5"
              data-testid="event-participants"
              aria-label={t('calendar.details.participants')}
            >
              {event.participants.map((name) => (
                <li
                  key={name}
                  className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[12px] font-medium text-foreground"
                >
                  <Users aria-hidden className="size-3 text-muted-foreground" />
                  {name}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {event.description !== null ? (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {t('calendar.details.description')}
            </p>
            <p className="mt-0.5 whitespace-pre-line break-words text-foreground">
              {event.description}
            </p>
          </div>
        ) : null}

        {event.source !== 'local' ? (
          <p
            className="rounded-lg border border-border bg-muted/40 p-2.5 text-[11.5px] text-muted-foreground"
            data-testid="event-readonly-note"
          >
            {t('calendar.details.readonly', { source: sourceLabel(event.source, t) })}
          </p>
        ) : null}
      </div>

      <SheetFooter className="border-t border-border px-5 py-4 sm:justify-between">
        {event.source === 'local' ? (
          confirming ? (
            <div
              className="flex w-full items-center justify-between gap-2"
              data-testid="delete-confirm"
            >
              <p className="text-[12px] text-muted-foreground">
                {t('calendar.details.delete_confirm_body', { title: event.title })}
              </p>
              <div className="flex shrink-0 gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-[12.5px]"
                  onClick={() => setConfirming(false)}
                >
                  {t('calendar.details.close')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-8 gap-1 text-[12.5px]"
                  disabled={deleting}
                  onClick={() => void handleDelete()}
                >
                  {deleting ? (
                    <Loader2 aria-hidden className="size-3.5 animate-spin" />
                  ) : (
                    <Trash2 aria-hidden className="size-3.5" />
                  )}
                  {t('calendar.details.confirm_delete')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-[12.5px] text-muted-foreground hover:text-red-400"
                data-testid="event-details-delete"
                onClick={() => setConfirming(true)}
              >
                <Trash2 aria-hidden className="size-3.5" />
                {t('calendar.details.delete')}
              </Button>
            </div>
          )
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          {event.source === 'local' && !confirming ? (
            <Button
              size="sm"
              className="h-8 gap-1.5 text-[12.5px] font-semibold"
              data-testid="event-details-edit"
              onClick={onEdit}
            >
              <Pencil aria-hidden className="size-3.5" />
              {t('calendar.details.edit')}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" className="h-8 text-[12.5px]" onClick={onClose}>
            {t('calendar.details.close')}
          </Button>
        </div>
      </SheetFooter>
    </div>
  );
}
